// ============================================================================
// OCR SERVICE — Personal Finance V1.0 (17/07/2026)
// Leitura de prints de tela (extratos, faturas, comprovantes) via Tesseract.js
// + parse inteligente + mapeamento de categorias por palavra-chave.
// ============================================================================

import Tesseract from "tesseract.js";

// ============================================================================
// MAPEAMENTO INTELIGENTE DE CATEGORIAS
// ============================================================================

const CATEGORY_KEYWORDS = [
  // ALIMENTACAO
  {
    keywords: [
      "mercado",
      "supermercado",
      "carrefour",
      "condor",
      "muffato",
      "anglo",
      "hiper",
    ],
    group: "ALIMENTACAO",
    sub: "Mercado",
  },
  {
    keywords: ["feira", "sacolao", "verdura", "fruta"],
    group: "ALIMENTACAO",
    sub: "Feira / Açougue",
  },
  {
    keywords: ["peixaria", "peixe", "camarao"],
    group: "ALIMENTACAO",
    sub: "Peixaria",
  },
  {
    keywords: [
      "restaurante",
      "lanche",
      "pizza",
      "hamburguer",
      "dogao",
      "madero",
    ],
    group: "ALIMENTACAO",
    sub: "Restaurante",
  },
  {
    keywords: ["pop house", "casa do queijo"],
    group: "ALIMENTACAO",
    sub: "PopHouse",
  },

  // MORADIA
  {
    keywords: ["aluguel", "condominio", "condomínio"],
    group: "MORADIA",
    sub: "Aluguel / Condomínio",
  },
  {
    keywords: ["copel", "energia", "eletrica"],
    group: "MORADIA",
    sub: "Energia Elétrica",
  },
  {
    keywords: ["internet", "net", "claro", "vivo", "oi fibra"],
    group: "MORADIA",
    sub: "Internet",
  },
  {
    keywords: ["celular", "tim", "vivo", "claro"],
    group: "MORADIA",
    sub: "Celular",
  },
  { keywords: ["gas", "cozinha"], group: "MORADIA", sub: "Gás de Cozinha" },
  {
    keywords: ["diarista"],
    group: "MORADIA",
    sub: "Diarista",
  },

  // TRANSPORTE
  {
    keywords: ["posto", "combustivel", "gasolina", "etanol", "diesel"],
    group: "TRANSPORTE",
    sub: "Combustível",
  },
  {
    keywords: ["oficina", "mecanico", "manutencao"],
    group: "TRANSPORTE",
    sub: "Oficina (Mecânico/Manutenção)",
  },
  {
    keywords: ["seguro", "auto", "carro"],
    group: "TRANSPORTE",
    sub: "Seguro do Carro",
  },
  {
    keywords: ["estacionamento", "park"],
    group: "TRANSPORTE",
    sub: "Estacionamento",
  },
  { keywords: ["uber", "99taxi"], group: "TRANSPORTE", sub: "Uber" },
  {
    keywords: ["spin", "financiamento"],
    group: "TRANSPORTE",
    sub: "Financiamento Spin",
  },

  // SAUDE
  { keywords: ["unimed", "saude"], group: "SAUDE", sub: "Unimed" },
  { keywords: ["dental", "amil"], group: "SAUDE", sub: "Amil Dental" },
  {
    keywords: ["farmacia", "drogasil", "drogaraia", "droga", "remedio"],
    group: "SAUDE",
    sub: "Remédio Avulso",
  },
  { keywords: ["terapia", "psicologo"], group: "SAUDE", sub: "Terapia" },
  { keywords: ["pilates", "academia"], group: "SAUDE", sub: "Academia" },
  {
    keywords: [
      "forxiga",
      "glifage",
      "rosucor",
      "alopurinol",
      "rivotril",
      "selozok",
      "prysma",
      "donaren",
    ],
    group: "SAUDE",
    sub: "Remédio Avulso",
  },

  // PETS
  {
    keywords: ["pet", "cao", "cachorro", "gato", "racao", "veterinario"],
    group: "PETS",
    sub: "Alimentação Natural",
  },
  {
    keywords: ["tosa", "banho", "petshop"],
    group: "PETS",
    sub: "Banho / Tosa",
  },
  { keywords: ["hotelzinho", "creche pet"], group: "PETS", sub: "Hotelzinho" },

  // ESTILO
  {
    keywords: [
      "netflix",
      "spotify",
      "prime video",
      "disney",
      "streaming",
      "hbo",
      "apple tv",
    ],
    group: "ESTILO",
    sub: "Streaming",
  },
  {
    keywords: [
      "google",
      "youtube",
      "icloud",
      "office",
      "adobe",
      "chatgpt",
      "midjourney",
      "canva",
    ],
    group: "ESTILO",
    sub: "Assinaturas & Ferramentas Online",
  },
  {
    keywords: ["faculdade", "uninter", "pos"],
    group: "ESTILO",
    sub: "Faculdade",
  },
  {
    keywords: [
      "vestuario",
      "roupa",
      "tenis",
      "calcado",
      "renner",
      "riachuelo",
      "marisa",
      "c&a",
    ],
    group: "ESTILO",
    sub: "Vestuário",
  },
  {
    keywords: ["viagem", "hotel", "airbnb", "passagem", "aviao"],
    group: "ESTILO",
    sub: "Viagem",
  },
  {
    keywords: [
      "cuidado",
      "barbearia",
      "salão",
      "beleza",
      "perfume",
      "cosmetico",
    ],
    group: "ESTILO",
    sub: "Cuidados Pessoais",
  },

  // FAMILIA
  {
    keywords: ["heranca", "pai", "inventario"],
    group: "FAMILIA",
    sub: "Acordo Herança Pai",
  },
  {
    keywords: ["reembolso", "eliane"],
    group: "FAMILIA",
    sub: "Reembolso Eliane",
  },

  // RECEITAS
  {
    keywords: ["retirada", "socio", "prolabore", "pro-labore"],
    group: "RECEITAS",
    sub: "Retirada de Sócio",
  },
  {
    keywords: ["salario", "holerite"],
    group: "RECEITAS",
    sub: "Salário Líquido",
  },
  {
    keywords: ["dividendo", "rendimento", "aplicacao"],
    group: "RECEITAS",
    sub: "Dividendos",
  },
];

// ============================================================================
// HELPERS
// ============================================================================

/** Sanitiza string OCR bruta — remove ruídos comuns */
function sanitizeOcrText(text) {
  return (text || "")
    .replace(/[|¦!¡¿?@#$%^*_={}[\]\\<>~`]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Tenta extrair data no formato DD/MM ou DD/MM/YYYY de uma linha */
function tryExtractDate(text) {
  const match = text.match(/(\d{2})\/(\d{2})(?:\/(\d{4}))?/);
  if (!match) return null;
  const [, d, m, y] = match;
  const year = y || new Date().getFullYear().toString();
  return `${year}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

/** Tenta extrair valor monetário R$ X.XXX,XX de uma linha */
function tryExtractValue(text) {
  const cleaned = text.replace(/\./g, "").replace(",", ".");
  const match = cleaned.match(/R?\$?\s*([\d.]+(?:\.\d{2})?)/);
  if (!match) return null;
  const raw = match[1].replace(/[^0-9.]/g, "");
  const val = parseFloat(raw);
  return isNaN(val) ? null : Math.round(val * 100) / 100;
}

/** Tenta extrair descricao — o que sobrou depois de remover data e valor */
function tryExtractDescription(tokens, dateTokens, valueTokens) {
  const filtered = tokens.filter(
    (t, i) => !dateTokens.includes(i) && !valueTokens.includes(i),
  );
  return filtered.join(" ").trim() || "";
}

/** Detecta se parece uma linha de cabecalho (header) */
function isHeaderLine(text) {
  const headers = [
    "data",
    "valor",
    "descricao",
    "lançamento",
    "historico",
    "debito",
    "credito",
    "saldo",
    "competencia",
    "documento",
  ];
  const lower = text.toLowerCase();
  return headers.some((h) => lower.startsWith(h) || lower.includes(h));
}

/** Detecta se parece linha de totalizacao */
function isFooterLine(text) {
  const lower = text.toLowerCase();
  return (
    lower.includes("total") ||
    lower.includes("saldo") ||
    lower.includes("soldor") ||
    lower.includes("soma") ||
    /^\s*[-–—]+\s*$/.test(text)
  );
}

/** Detecta conta com base no texto */
function detectAccount(text) {
  const lower = text.toLowerCase();
  if (lower.includes("c6") || lower.includes("pix")) return "C6 PJ (Pix)";
  if (lower.includes("itau") || lower.includes("itaú")) return "Itaú PF";
  if (lower.includes("nubank")) return "Nubank PF";
  if (lower.includes("inter")) return "Banco Inter";
  if (lower.includes("bradesco")) return "Bradesco";
  if (lower.includes("santander")) return "Santander";
  if (lower.includes("caixa")) return "Caixa Econômica";
  if (lower.includes("picpay")) return "PicPay";
  if (lower.includes("mercadopago") || lower.includes("mercado pago"))
    return "Mercado Pago";
  if (
    lower.includes("elo") ||
    lower.includes("visa") ||
    lower.includes("master")
  )
    return "Cartão de Crédito";
  return "C6 PJ (Pix)";
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Extrai texto de uma imagem usando Tesseract.js
 * @param {File|Blob} imageFile - Arquivo de imagem
 * @param {object} [options] - Opcoes
 * @param {string} [options.lang='por'] - Idioma OCR
 * @param {function} [options.onProgress] - Callback de progresso (p => {})
 * @returns {Promise<string>} Texto extraido
 */
export async function extractTextFromImage(imageFile, options = {}) {
  const lang = options.lang || "por";
  const onProgress = options.onProgress || (() => {});

  const result = await Tesseract.recognize(imageFile, lang, {
    logger: (m) => {
      if (m.status === "recognizing text") {
        onProgress(m.progress || 0);
      }
    },
  });

  return result.data.text;
}

/**
 * Parseia o texto OCR bruto em lancamentos estruturados.
 * @param {string} rawText - Texto extraido pelo OCR
 * @returns {Array<{date: string, value: number, description: string, group: string, sub: string, account: string}>}
 */
export function parseOcrText(rawText) {
  if (!rawText || !rawText.trim()) return [];

  const lines = rawText
    .split("\n")
    .map((l) => sanitizeOcrText(l))
    .filter((l) => l.length > 5);

  const results = [];

  for (const line of lines) {
    // Pula cabecalhos e rodapes
    if (isHeaderLine(line)) continue;
    if (isFooterLine(line)) continue;
    if (/^\d{2}\/\d{2}\/\d{4}\s+/.test(line) && /\d+[,.]\d{2}$/.test(line))
      continue; // linha de saldo

    const lower = line.toLowerCase();
    if (
      lower.includes("extrato") ||
      lower.includes("banco") ||
      lower.includes("conta corrente") ||
      lower.includes("agência") ||
      lower.includes("agencia") ||
      lower.includes("cpf") ||
      lower.includes("cnpj")
    )
      continue;

    const tokens = line.split(/\s+/).filter((t) => t);
    const dateTokens = [];
    const valueTokens = [];

    // 1. Extrai data
    let date = tryExtractDate(line);
    tokens.forEach((t, i) => {
      if (/\d{2}\/\d{2}(?:\/\d{4})?/.test(t)) dateTokens.push(i);
    });

    // 2. Extrai valor
    let value = tryExtractValue(line);
    tokens.forEach((t, i) => {
      if (/R?\$?\s*[\d.,]+/.test(t)) valueTokens.push(i);
    });

    // 3. Se nao achou data nem valor, pula
    if (!date && !value) continue;

    // 4. Descricao = tokens restantes
    let description = tryExtractDescription(tokens, dateTokens, valueTokens);
    if (!description) {
      // Fallback: usa toda a linha sem data/valor
      description = line
        .replace(/\d{2}\/\d{2}(?:\/\d{4})?/g, "")
        .replace(/R?\$?\s*[\d.,]+/g, "")
        .trim();
    }
    if (!description || description.length < 2) continue;

    // 5. Mapeia categoria
    const category = mapCategory(description);

    // 6. Detecta conta
    const account = detectAccount(line + " " + description);

    results.push({
      date: date || new Date().toISOString().slice(0, 10),
      value,
      description: description.substring(0, 200),
      group: category.group,
      sub: category.sub,
      account,
    });
  }

  return results;
}

/**
 * Mapeia uma descricao para {group, sub} baseado em keywords.
 * @param {string} description
 * @returns {{group: string, sub: string}}
 */
export function mapCategory(description) {
  const lower = description.toLowerCase();
  for (const rule of CATEGORY_KEYWORDS) {
    for (const kw of rule.keywords) {
      if (lower.includes(kw)) {
        return { group: rule.group, sub: rule.sub };
      }
    }
  }
  return { group: "ALIMENTACAO", sub: "Avulsos" };
}

/**
 * Converte lancamentos extraidos para o formato esperado pelo BatchImportPanel.
 * @param {Array} parsedRows - Resultado de parseOcrText()
 * @returns {Array} Rows no formato BatchImportPanel
 */
export function toBatchRows(parsedRows) {
  const today = new Date().toISOString().slice(0, 10);
  const currMonth = today.slice(0, 7);

  return parsedRows.map((r, i) => {
    const date = r.date || today;
    const value = r.value || 0;
    const formattedValue =
      value > 0
        ? value.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "";

    return {
      id: `ocr-${Date.now()}-${i}`,
      docId: null,
      group: r.group || "ALIMENTACAO",
      subCategory: r.sub || "",
      templateId: "",
      natureza: "PF",
      sudNivel1: r.description?.includes("PJ") ? "Recta Scripta Editora" : "",
      sudNivel2: "",
      sudNivel3: "",
      sudVars: "",
      descIn: "",
      descOut: r.description || "",
      account: r.account || "C6 PJ (Pix)",
      valorPrevisto: "",
      valorReal: formattedValue,
      date,
      juros: "0,00",
      desconto: "0,00",
      acrescimo: "0,00",
      tarifa: "0,00",
      modoLancamento: "UNICO",
      parcelaNumero: "",
      parcelaTotal: "",
      recorrencia: "Mensal",
      repeticoes: "12",
      numeroDocumento: "",
      dataEmissao: "",
      competenceDate: currMonth,
      settlementDate: date,
      statusType: "PENDING",
      needsSettlement: true,
      status: "pending",
      errorMsg: "",
      vinculo: "",
    };
  });
}
